import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Pencil,
  Trash2,
  Check,
  Clock,
  RefreshCw,
  Truck,
  PackageCheck,
  Ban,
  Hash,
  User,
  UserCheck,
  Calendar,
} from "lucide-react";
import SearchableSelect from "../../components/SearchableSelect";
import { apiClient } from "../../apiclient/apiclient";
import { formatAnyDateToDDMMYYYY } from "../../utils/dateFormatter";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

const EditOrderModal = ({ order, onClose, onSave, viewOnly = false }) => {
  const [form, setForm] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [originalItems, setOriginalItems] = useState([]);
  const [rawProducts, setRawProducts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [isWhDropdownOpen, setIsWhDropdownOpen] = useState(false);
  const whDropdownRef = useRef(null);

  const inputClass =
    "w-full border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition disabled:bg-slate-50 disabled:text-slate-500";
  const labelClass = "block text-xs font-medium text-slate-700 mb-0.5";


  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [staffList, setStaffList] = useState([]);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const prodRes = await apiClient.get("/products");
        setRawProducts(prodRes.data || []);
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };

    const loadDeliveryPersons = async () => {
      try {
        const dpRes = await apiClient.get("/delivery-persons");
        setDeliveryPersons(dpRes.data || []);
      } catch (err) {
        console.error("Failed to load delivery persons", err);
      }
    };

    const loadStaffList = async () => {
      try {
        const staffRes = await apiClient.get("/all");
        setStaffList(staffRes.data?.users || []);
      } catch (err) {
        console.error("Failed to load staff list", err);
      }
    };

    loadProducts();
    loadDeliveryPersons();
    loadStaffList();
  }, []);

  // Click outside listener for custom warehouse select dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (whDropdownRef.current && !whDropdownRef.current.contains(event.target)) {
        setIsWhDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* ================= PRODUCT OPTIONS ================= */
  const seenKeys = new Set();
  const productOptions = [];

  // Ensure current order items are always in options so they display correctly
  if (form?.items) {
    form.items.forEach((item) => {
      const key = `${item.productName}-${item.quantityValue}-${item.quantityUnit}`;
      seenKeys.add(key);
      productOptions.push({
        _id: item.productId,
        name: item.productName,
        displayName: `${item.productName} – ${item.quantityValue}${item.quantityUnit}`,
        quantityUnit: item.quantityUnit,
        quantityValue: item.quantityValue,
        price: item.price,
      });
    });
  }

  rawProducts.forEach((p) => {
    const key = `${p.name}-${p.quantityValue}-${p.quantityUnit}`;
    if (!seenKeys.has(key) && !productOptions.some(o => o._id === p._id)) {
      seenKeys.add(key);
      productOptions.push({
        _id: p._id,
        name: p.name,
        displayName: `${p.name} – ${p.quantityValue}${p.quantityUnit}`,
        quantityUnit: p.quantityUnit,
        quantityValue: p.quantityValue,
        price: p.price,
      });
    }
  });

  /* ================= INIT FORM ================= */
  useEffect(() => {
    if (!order) return;

    const rebuiltItems = order.items.map((it) => ({
      ...it,
      productId: typeof it.productId === 'object' && it.productId !== null ? it.productId._id : it.productId,
      warehouseId: typeof it.warehouseId === 'object' && it.warehouseId !== null ? it.warehouseId._id : (it.warehouseId || ""),
    }));

    setForm({
      ...order,
      newStatus: order.status,
      items: rebuiltItems,
      paymentDetails: {
        totalAmount: order.paymentDetails?.totalAmount || 0,
        discount: order.paymentDetails?.discount || 0,
        paidAmount: order.paymentDetails?.paidAmount || 0,
        balanceAmount: order.paymentDetails?.balanceAmount || 0,
        paymentStatus: order.paymentDetails?.paymentStatus || "unpaid",
      },
    });

    setOriginalItems(JSON.parse(JSON.stringify(rebuiltItems)));
  }, [order]);
  useEffect(() => {
    if (!form) return;

    const totalAmount = Number(form.items.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
      0,
    ).toFixed(2));

    const discount = Number(form.paymentDetails.discount || 0);
    const paid = Number(form.paymentDetails.paidAmount || 0);

    const payable = Number(Math.max(totalAmount - discount, 0).toFixed(2));
    const balanceAmount = Number(Math.max(payable - paid, 0).toFixed(2));

    let paymentStatus = "unpaid";
    if (balanceAmount === 0) paymentStatus = "paid";
    else if (paid > 0) paymentStatus = "partial";

    setForm((prev) => ({
      ...prev,
      paymentDetails: {
        ...prev.paymentDetails,
        totalAmount,
        balanceAmount,
        paymentStatus,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.items, form?.paymentDetails?.discount, form?.paymentDetails?.paidAmount]);

  if (!form) return null;

  const currentUser = JSON.parse(
    localStorage.getItem("agent") ||
    localStorage.getItem("user") ||
    localStorage.getItem("auth") ||
    "{}"
  );
  const isSuperAdmin = currentUser?.role?.toLowerCase() === "superadmin";

  const isLocked = viewOnly || (order.status === "cancelled") || (order.status === "completed" && !isSuperAdmin);

  const itemsEditable = !viewOnly && !isLocked && (order.status === "pending" || (order.status === "completed" && isSuperAdmin));
  const statusEditable = !viewOnly && !isLocked;
  const paymentEditable = itemsEditable;

  /* ================= HELPERS ================= */
  const updateField = (index, field, value) => {
    if (!itemsEditable) return;
    const updated = [...form.items];
    updated[index][field] = value;
    setForm({ ...form, items: updated });
  };

  const addNewItem = () => {
    if (!itemsEditable) return;
    setForm({
      ...form,
      items: [
        ...form.items,
        {
          productId: "",
          productName: "",
          quantity: 1,
          quantityValue: "",
          quantityUnit: "",
          price: 0,
          totalPrice: 0,
          warehouseId: "",
          warehouseName: "",
          warehouseAddress: "",
          expiryDate: null,
          batchId: "",
          staffReference: null,
        },
      ],
    });
    setEditingIndex(form.items.length);
  };

  const deleteItem = (index) => {
    if (!itemsEditable) return;
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index),
    });
  };

  const cancelInlineEdit = (index) => {
    if (!originalItems[index]) {
      deleteItem(index);
    } else {
      const updated = [...form.items];
      updated[index] = originalItems[index];
      setForm({ ...form, items: updated });
    }
    setEditingIndex(null);
  };

  const startInlineEdit = (index) => {
    if (!itemsEditable) return;
    setEditingIndex(index);
  };

  /* ================= SAVE ================= */
  const saveOrder = async () => {
    if (isLocked || isSavingRef.current) return;

    try {
      const dpId = typeof form.deliveryPersonId === 'object' && form.deliveryPersonId !== null
        ? form.deliveryPersonId._id
        : form.deliveryPersonId || null;

      const staffRefId = typeof form.staffReference === 'object' && form.staffReference !== null
        ? form.staffReference._id
        : form.staffReference || null;

      // 🔒 STATUS ONLY UPDATE
      if (!itemsEditable) {
        isSavingRef.current = true;
        setIsSaving(true);
        await onSave({
          _id: form._id,
          status: form.newStatus,
          deliveryPersonId: dpId,
          staffReference: staffRefId
        });
        return;
      }

      // 🛑 VALIDATE ITEMS BEFORE SAVING
      if (!form.items || form.items.length === 0) {
        toast.error("Please add at least one item to the order.");
        return;
      }

      for (let i = 0; i < form.items.length; i++) {
        const item = form.items[i];
        if (!item.productId) {
          toast.error(`Please select a product for item #${i + 1}`);
          return;
        }
        if (!item.warehouseId) {
          toast.error(`Please select a warehouse and batch/expiry for ${item.productName || "item #" + (i + 1)}`);
          return;
        }
        if (Number(item.quantity || 0) <= 0) {
          toast.error(`Quantity must be greater than 0 for ${item.productName || "item #" + (i + 1)}`);
          return;
        }
      }

      isSavingRef.current = true;
      setIsSaving(true);

      const updatedItems = form.items.map((item) => {
        const price = Number(item.price || 0);
        const qty = Number(item.quantity || 0);
        const itemStaffRefId = typeof item.staffReference === 'object' && item.staffReference !== null
          ? item.staffReference._id
          : item.staffReference || null;

        return {
          productId: item.productId,
          productName: item.productName,
          quantity: qty,
          quantityValue: item.quantityValue,
          quantityUnit: item.quantityUnit, // ✅ SAME AS CREATE ORDER
          price,
          totalPrice: price * qty,
          warehouseId: item.warehouseId || null, // Map empty strings to null to prevent CastErrors
          warehouseName: item.warehouseName,
          warehouseAddress: item.warehouseAddress,
          expiryDate: item.expiryDate || null,
          batchId: item.batchId || "",
          staffReference: itemStaffRefId,
        };
      });

      await onSave({
        _id: form._id,
        status: form.newStatus,
        createdAt: form.createdAt,
        items: updatedItems,
        deliveryPersonId: dpId,
        staffReference: updatedItems[0]?.staffReference || staffRefId || null,
        paymentDetails: {
          discount: Number(form.paymentDetails.discount || 0),
          paidAmount: Number(form.paymentDetails.paidAmount || 0),
        },
      });
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  };
  const getWarehouseOptions = (productId, index) => {
    const product = rawProducts.find((p) => p._id === productId);
    if (!product) return [];

    return (product.warehouses || []).map((w, idx) => {
      const alreadyUsed = form.items
        .filter(
          (it, i) =>
            i !== index &&
            it.productId === productId &&
            it.warehouseId === (w.location?._id || w.location || "") &&
            (it.expiryDate ? new Date(it.expiryDate).getTime() === new Date(w.expiryDate || 0).getTime() : !w.expiryDate)
        )
        .reduce((sum, it) => sum + Number(it.quantity || 0), 0);

      return {
        selectionKey: `${w.location?._id || w.location || ""}_${w.expiryDate || "no-expiry"}_${idx}`,
        warehouseId: w.location?._id || w.location || "",
        warehouseName: w.location?.name || w.locationName || "Unknown Warehouse",
        warehouseAddress: w.location?.address || "",
        expiryDate: w.expiryDate,
        batchId: w.batchId,
        stock: Math.max(Number(w.quantity || 0) - alreadyUsed, 0),
      };
    });
  };

  /* ================= UI ================= */
  return (
    <div className="fixed inset-0 bg-black/40 z-[100] md:ml-64 mt-16 pb-0 flex flex-col animate-in fade-in duration-300">
      <div className="bg-white rounded-none shadow-xl w-full h-full flex flex-col overflow-hidden">
        {/* HEADER */}
        <div className="px-6 py-3 bg-[#172554] flex justify-between items-center text-white sticky top-0 z-20">
          <h2 className="text-lg font-semibold">
            {viewOnly ? "Order Details" : "Edit Order"}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-white">
          {/* TOP INFO CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mb-0">
            {/* Main Metadata Cards in Clean 4-Column Layout */}
            <div className="md:col-span-2 bg-white border-b border-e p-4 flex flex-wrap md:flex-nowrap justify-around items-center gap-4">
              {/* Order ID */}
              <div className="flex flex-col gap-1 min-w-[90px]">
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Hash size={14} />
                  <span className="text-[10px] font-medium uppercase tracking-wider">
                    Order ID
                  </span>
                </div>
                <p className="font-bold text-slate-900 text-sm">
                  {form.orderId}
                </p>
              </div>

              {/* Client */}
              <div className="flex flex-col gap-1 min-w-[130px] flex-1">
                <div className="flex items-center gap-1.5 text-slate-400">
                  <User size={14} />
                  <span className="text-[10px] font-medium uppercase tracking-wider">
                    Client
                  </span>
                </div>
                <p
                  className="font-bold text-slate-900 text-sm truncate"
                  title={form.clientId?.name}
                >
                  {form.clientId?.name || "Walk-in Client"}
                </p>
              </div>

              {/* Agent / Delivery Person */}
              <div className="flex flex-col gap-1 min-w-[140px] flex-1">
                <div className="flex items-center gap-1.5 text-slate-400">
                  <UserCheck size={14} />
                  <span className="text-[10px] font-medium uppercase tracking-wider">
                    Agent
                  </span>
                </div>
                {!isLocked ? (
                  <select
                    value={
                      typeof form.deliveryPersonId === 'object' && form.deliveryPersonId !== null
                        ? form.deliveryPersonId._id
                        : form.deliveryPersonId || ""
                    }
                    onChange={(e) =>
                      setForm({ ...form, deliveryPersonId: e.target.value })
                    }
                    className="w-full bg-transparent border-b border-dashed border-slate-400 font-bold text-slate-900 text-sm outline-none cursor-pointer pb-0.5"
                  >
                    <option value="">Unassigned</option>
                    {(() => {
                      const optionsToRender = [...deliveryPersons];
                      const currentAgent = typeof form.deliveryPersonId === 'object' ? form.deliveryPersonId : null;
                      if (currentAgent && currentAgent._id && !optionsToRender.some(dp => dp._id === currentAgent._id)) {
                        optionsToRender.push(currentAgent);
                      }
                      return optionsToRender.map((dp) => (
                        <option key={dp._id} value={dp._id}>
                          {dp.name}
                        </option>
                      ));
                    })()}
                  </select>
                ) : (
                  <p className="font-bold text-slate-900 text-sm truncate">
                    {form.deliveryPersonId?.name || "N/A"}
                  </p>
                )}
              </div>

              {/* Date */}
              <div className="flex flex-col gap-1 min-w-[110px]">
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Calendar size={14} />
                  <span className="text-[10px] font-medium uppercase tracking-wider">
                    Date
                  </span>
                </div>
                {itemsEditable ? (
                  <div className="w-full">
                    <DatePicker
                      selected={new Date(form.createdAt)}
                      onChange={(date) => {
                        if (date) {
                          setForm({ ...form, createdAt: date.toISOString() });
                        }
                      }}
                      dateFormat="dd/MM/yyyy"
                      maxDate={new Date()}
                      className="font-bold text-slate-900 text-sm border-b border-dashed border-slate-400 bg-transparent outline-none w-full cursor-pointer p-0 m-0"
                      wrapperClassName="w-full"
                    />
                  </div>
                ) : (
                  <p className="font-bold text-slate-900 text-sm">
                    {formatAnyDateToDDMMYYYY(form.createdAt)}
                  </p>
                )}
              </div>
            </div>

            {/* Status Control Card */}
            <div className="border-b p-4 flex flex-col justify-center items-center">
              <label className={labelClass}>Update Status</label>
              <div className="flex flex-wrap gap-3 mt-1.5 justify-center">
                {[
                  {
                    id: "pending",
                    icon: Clock,
                    color: "blue",
                    label: "Pending",
                  },
                  {
                    id: "processing",
                    icon: RefreshCw,
                    color: "indigo",
                    label: "Processing",
                  },
                  {
                    id: "shipped",
                    icon: Truck,
                    color: "purple",
                    label: "Shipped",
                  },
                  {
                    id: "delivered",
                    icon: PackageCheck,
                    color: "emerald",
                    label: "Delivered",
                  },
                  {
                    id: "completed",
                    icon: Check,
                    color: "green",
                    label: "Completed",
                  },
                  {
                    id: "cancelled",
                    icon: Ban,
                    color: "red",
                    label: "Cancelled",
                  },
                ].map((st) => {
                  const isActive = form.newStatus === st.id;
                  const Icon = st.icon;
                  return (
                    <button
                      key={st.id}
                      type="button"
                      disabled={!statusEditable}
                      onClick={() => setForm({ ...form, newStatus: st.id })}
                      title={st.label}
                      className={`w-9 h-9 flex items-center justify-center rounded-full border-2 transition-all duration-200 ${isActive
                        ? "bg-orange-500 border-orange-500 text-white scale-110"
                        : "bg-white border-slate-200 text-slate-400 hover:border-orange-500 hover:text-orange-500"
                        } ${!statusEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-center md:text-left">
                <span className="text-[10px] font-bold uppercase text-[#172554] bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                  Current: {form.newStatus}
                </span>
              </div>
              {isLocked && (
                <p className="text-[10px] text-red-500 mt-1 font-medium italic text-center md:text-left">
                  Locked: {order.status} orders cannot be modified.
                </p>
              )}
            </div>
          </div>

          {/* ITEMS HEADER (CLONED FROM VIEW ORDERS) */}
          <div className="w-full border-y py-2 px-4 border-slate-200 bg-blue-50 flex items-center justify-between mb-0 mt-0">
            <div>
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-tight">
                Order Items
              </h3>
              <p className="text-[10px] text-slate-500 font-medium">
                Manage products and quantities in this shipment
              </p>
            </div>
            {itemsEditable && (
              <button
                onClick={addNewItem}
                className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-xs font-bold transition-all shadow-sm flex items-center gap-1"
              >
                + Add Item
              </button>
            )}
          </div>

          <div className="bg-white">
            {form.items.map((item, idx) => (
              <div key={idx} className="border-b px-10 py-4">
                {editingIndex === idx && itemsEditable ? (
                  <div className="flex flex-row gap-3 items-center w-full">
                    {/* PRODUCT - Flexible Width */}
                    <div className="flex-1 min-w-[200px]">
                      <SearchableSelect
                        options={productOptions}
                        value={item.productId}
                        ClassName={inputClass}
                        onChange={(id) => {
                          if (item.productId === id) return; // Prevent clearing warehouse if same product selected
                          const p = productOptions.find((x) => x._id === id);
                          updateField(idx, "productId", id);
                          updateField(idx, "productName", p.name);
                          updateField(idx, "price", p.price);
                          updateField(idx, "quantityUnit", p.quantityUnit);
                          updateField(idx, "quantityValue", p.quantityValue);
                          updateField(idx, "warehouseId", "");
                          updateField(idx, "warehouseName", "");
                          updateField(idx, "warehouseAddress", "");
                          updateField(idx, "expiryDate", null);
                          updateField(idx, "batchId", "");
                        }}
                      />
                    </div>

                    {/* ✅ WAREHOUSE - Fixed/Stable Width with Expiry */}
                    <div className="w-64 relative" ref={whDropdownRef}>

                      {/* Trigger Button */}
                      <button
                        type="button"
                        onClick={() => setIsWhDropdownOpen(!isWhDropdownOpen)}
                        disabled={!item.productId}
                        className={`w-full h-10 border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition flex justify-between items-center text-slate-800 text-left shadow-sm ${!item.productId ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50/30"}`}
                      >
                        {(() => {
                          const whOptions = getWarehouseOptions(item.productId, idx);
                          const selectedWh = whOptions.find(
                            (w) =>
                              w.warehouseId === item.warehouseId &&
                              (item.expiryDate ? new Date(item.expiryDate).getTime() === new Date(w.expiryDate || 0).getTime() : !w.expiryDate)
                          );

                          if (selectedWh) {
                            let dateStr = "No Expiry";
                            if (selectedWh.expiryDate) {
                              try {
                                const d = new Date(selectedWh.expiryDate);
                                if (!isNaN(d.getTime())) {
                                  dateStr = `Exp: ${d.toLocaleDateString()}`;
                                }
                              } catch (e) { }
                            }
                            const batchStr = selectedWh.batchId && selectedWh.batchId !== "N/A" ? selectedWh.batchId : "";

                            return (
                              <div className="flex justify-between items-center w-full min-w-0 pr-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="font-extrabold text-[#172554] truncate text-xs">
                                    {selectedWh.warehouseName}
                                  </span>
                                  {batchStr && (
                                    <span className="font-mono text-[9px] bg-slate-100 px-1 py-0.2 rounded border border-slate-200 text-slate-500 shrink-0">
                                      {batchStr}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-[10px] text-slate-500 font-bold shrink-0">{dateStr}</span>
                                  <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-black border border-blue-100 uppercase shrink-0">
                                    {selectedWh.stock} left
                                  </span>
                                </div>
                              </div>
                            );
                          }
                          return <span className="text-slate-400">Warehouse & Expiry...</span>;
                        })()}
                        <span className="text-slate-400 text-[10px] ml-2 shrink-0">▼</span>
                      </button>

                      {/* Absolute Dropdown List */}
                      {isWhDropdownOpen && (
                        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto z-[200] divide-y divide-slate-100 animate-in fade-in slide-in-from-top-1 duration-150">
                          {(() => {
                            const whOptions = getWarehouseOptions(item.productId, idx);

                            // Group options by warehouseId
                            const groups = {};
                            whOptions.forEach((wh) => {
                              const key = wh.warehouseId || "unknown";
                              if (!groups[key]) {
                                groups[key] = {
                                  name: wh.warehouseName || "Warehouse",
                                  items: [],
                                  minExpiry: Infinity,
                                };
                              }
                              groups[key].items.push(wh);

                              const expTime = wh.expiryDate ? new Date(wh.expiryDate).getTime() : Infinity;
                              if (expTime < groups[key].minExpiry) {
                                groups[key].minExpiry = expTime;
                              }
                            });

                            const groupList = Object.values(groups).sort((a, b) => a.minExpiry - b.minExpiry);

                            if (groupList.length === 0) {
                              return (
                                <div className="p-3 text-slate-400 text-xs text-center italic">
                                  No warehouse options found.
                                </div>
                              );
                            }

                            // Sort items chronologically inside each group and calculate total combined stock
                            groupList.forEach((g) => {
                              g.items.sort((a, b) => {
                                const dateA = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
                                const dateB = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;
                                return dateA - dateB;
                              });
                              g.totalQuantity = g.items.reduce((sum, whItem) => sum + Number(whItem.stock || 0), 0);
                            });

                            return groupList.map((group) => (
                              <div key={group.name} className="flex flex-col">
                                {/* Group Header Banner */}
                                <div className="bg-slate-50 px-3 py-1.5 font-extrabold text-[10px] text-[#172554] uppercase tracking-wider select-none border-b border-slate-200 flex justify-between items-center">
                                  <span>{group.name}</span>
                                  <span className="bg-[#172554] text-white px-2 py-0.5 rounded text-[9px] font-black shrink-0">
                                    {group.totalQuantity} Total
                                  </span>
                                </div>

                                {/* Items inside this group */}
                                <div className="divide-y divide-slate-50">
                                  {group.items.map((wh) => {
                                    let dateStr = "No Expiry";
                                    if (wh.expiryDate) {
                                      try {
                                        const d = new Date(wh.expiryDate);
                                        if (!isNaN(d.getTime())) {
                                          dateStr = `Exp: ${d.toLocaleDateString()}`;
                                        }
                                      } catch (e) { }
                                    }
                                    const batchStr = wh.batchId && wh.batchId !== "N/A" ? wh.batchId : "";

                                    return (
                                      <div
                                        key={wh.selectionKey}
                                        onClick={() => {
                                          updateField(idx, "warehouseId", wh.warehouseId);
                                          updateField(idx, "warehouseName", wh.warehouseName);
                                          updateField(idx, "warehouseAddress", wh.warehouseAddress);
                                          updateField(idx, "expiryDate", wh.expiryDate || null);
                                          updateField(idx, "batchId", wh.batchId || "");
                                          setIsWhDropdownOpen(false);
                                        }}
                                        className="flex justify-between items-center px-3.5 py-2 hover:bg-slate-50/50 cursor-pointer text-xs transition-colors"
                                      >
                                        <div className="flex flex-col gap-0.5">
                                          <span className={`font-bold ${wh.expiryDate ? "text-slate-700" : "text-slate-500"}`}>
                                            {dateStr}
                                          </span>
                                          {batchStr && (
                                            <span className="text-[9px] font-mono text-slate-400 leading-none">
                                              Batch: {batchStr}
                                            </span>
                                          )}
                                        </div>
                                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-black border border-blue-100 uppercase">
                                          {wh.stock} left
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>

                    {/* PRICE - Small Width */}
                    <div className="w-24">
                      <input
                        type="number"
                        placeholder="Price"
                        value={item.price}
                        onChange={(e) =>
                          updateField(idx, "price", e.target.value)
                        }
                        className={inputClass}
                      />
                    </div>

                    {/* QTY - Small Width */}
                    <div className="w-20">
                      <input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) =>
                          updateField(idx, "quantity", e.target.value)
                        }
                        className={inputClass}
                      />
                    </div>

                    {/* REFERENCE AGENT - Select Dropdown */}
                    <div className="w-44">
                      <select
                        value={
                          typeof item.staffReference === "object" && item.staffReference !== null
                            ? item.staffReference._id
                            : item.staffReference || ""
                        }
                        onChange={(e) => {
                          const staffId = e.target.value || null;
                          const staffObj = staffList.find((s) => s._id === staffId);
                          updateField(idx, "staffReference", staffObj || staffId);
                        }}
                        className={inputClass}
                      >
                        <option value="">Direct Sales (No Agent)</option>
                        {staffList.map((staff) => (
                          <option key={staff._id} value={staff._id}>
                            {staff.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* ACTIONS - Side by Side */}
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setEditingIndex(null)}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 transition shadow-sm shrink-0"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => cancelInlineEdit(idx)}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-200 text-slate-600 hover:bg-slate-300 transition shadow-sm shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center bg-white ">
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-800 text-sm">
                        {item.productName}
                      </span>
                      <span className="text-[10px] text-slate-500 font-medium">
                        {item.quantityValue} {item.quantityUnit} ×{" "}
                        <span className="text-blue-600 font-bold text-xs">
                          {item.quantity}
                        </span>
                        {" • "}
                        {item.warehouseName || "No Warehouse"}
                        {item.expiryDate && ` (Exp: ${new Date(item.expiryDate).toLocaleDateString()})`}
                        {item.batchId && item.batchId !== "N/A" && ` [Batch: ${item.batchId}]`}
                        {" • "}
                        <span className={`inline-flex items-center px-1.5 py-0.2 rounded font-semibold ${item.staffReference ? "bg-purple-50 text-purple-700 border border-purple-100" : "bg-slate-100 text-slate-600"}`}>
                          Ref: {typeof item.staffReference === "object" && item.staffReference?.name ? item.staffReference.name : (staffList.find((s) => s._id === item.staffReference)?.name || form.staffReference?.name || "Direct Sales (No Agent)")}
                        </span>
                      </span>
                    </div>

                    {itemsEditable && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => startInlineEdit(idx)}
                          className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#172554] bg-white text-[#172554] hover:border-[#172554] hover:bg-[#172554] hover:text-white transition "
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => deleteItem(idx)}
                          className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-orange-500 bg-white text-orange-500 hover:border-orange-500 hover:bg-orange-500 hover:text-white transition "
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* FINANCIAL SUMMARY HEADER (CLONED FROM ORDER ITEMS) */}
          <div className="w-full border-y py-2 px-4 border-slate-200 bg-blue-50 flex items-center justify-between mb-4 mt-0">
            <div>
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-tight">
                Financial Summary
              </h3>
              <p className="text-[10px] text-slate-500 font-medium">
                Review totals, discounts, and payment status
              </p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 px-10 pb-8">
            <div>
              <label className={labelClass}>Total</label>
              <input
                disabled
                value={Number(form.paymentDetails.totalAmount || 0).toFixed(2)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Discount</label>
              <input
                type="number"
                disabled={!paymentEditable}
                value={form.paymentDetails.discount}
                onChange={(e) =>
                  setForm({
                    ...form,
                    paymentDetails: {
                      ...form.paymentDetails,
                      discount: Number(e.target.value || 0),
                    },
                  })
                }
                className={inputClass}
              />
            </div>

            <div>
              <div>
                <label className={labelClass}>Paid</label>
                <input
                  type="number"
                  disabled={!paymentEditable}
                  value={form.paymentDetails.paidAmount}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      paymentDetails: {
                        ...form.paymentDetails,
                        paidAmount: Number(e.target.value || 0),
                      },
                    })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Balance</label>
              <input
                disabled
                value={Number(form.paymentDetails.balanceAmount || 0).toFixed(2)}
                className={inputClass}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t bg-white flex justify-end gap-3 sticky bottom-0 z-20">
          <button
            type="button"
            onClick={onClose}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
          >
            Cancel
          </button>
          {!viewOnly && !isLocked && (
            <button
              type="button"
              onClick={saveOrder}
              disabled={isSaving}
              className={`bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 shadow-sm ${isSaving ? "opacity-70 cursor-not-allowed" : ""
                }`}
            >
              {isSaving ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Saving...
                </>
              ) : (
                "Save Order"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditOrderModal;
